import { SectionCard } from "@/components/common/SectionCard";
import type { ServiceEvent } from "@/lib/service/types";
import {
  dangerBtnClass,
  fmtCents,
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "./serviceShared";
import { useServiceEventCost } from "./useServiceEventCost";

type LeafRow = {
  material_code: string;
  material_name: string;
  quantity: number;
  extended_cost_usd: number;
};

export function ServiceEventCostPanel({ event }: { event: ServiceEvent }) {
  const c = useServiceEventCost(event);
  const display = c.snapshot
    ? {
        estimated_labor_cents: c.snapshot.estimated_labor_cents,
        estimated_materials_cents: c.snapshot.estimated_materials_cents,
        estimated_total_cents: c.snapshot.estimated_total_cents,
      }
    : c.preview;
  const isFrozen = Boolean(c.snapshot);
  const leaves = (
    isFrozen ? (c.snapshot?.resolved_materials ?? []) : (c.preview?.resolved_materials ?? [])
  ) as LeafRow[];

  const actualTotalCents = c.actualLines.reduce((sum, line) => {
    const unit = line.unit_cost_cents ?? 0;
    return sum + Math.round(unit * line.quantity);
  }, 0);

  return (
    <SectionCard title="Job cost preview">
      {c.error ? (
        <p className="mb-2 text-sm text-red-700 dark:text-red-300">{c.error}</p>
      ) : null}

      {c.recipes.length > 1 && !isFrozen ? (
        <div className="mb-3">
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-zinc-400">
            Materials recipe
          </label>
          <select
            className={inputClass}
            value={c.recipeOverride || c.preview?.mfg_recipe_id || ""}
            onChange={(e) => c.setRecipeOverride(e.target.value)}
            disabled={c.busy}
          >
            <option value="">Auto-resolve</option>
            {c.recipes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.recipe_code} — {r.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {c.busy && !display ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Loading cost preview…</p>
      ) : display ? (
        <div className="space-y-2 text-sm text-gray-800 dark:text-zinc-200">
          {isFrozen ? (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Frozen at approval ({new Date(c.snapshot!.frozen_at).toLocaleString()})
            </p>
          ) : null}
          <div>
            <strong>Labor:</strong> {fmtCents(display.estimated_labor_cents)}
            {c.preview?.labor_rate_kind === "hourly" && c.preview.duration_min ? (
              <span className="text-xs text-gray-500 dark:text-zinc-400">
                {" "}
                (hourly × {c.preview.duration_min} min)
              </span>
            ) : null}
          </div>
          <div>
            <strong>Materials:</strong> {fmtCents(display.estimated_materials_cents)}
            {c.preview?.recipe_code ? (
              <span className="text-xs text-gray-500 dark:text-zinc-400">
                {" "}
                · {c.preview.recipe_code}
                {c.preview.rollup_mode === "nested_materials" ? " (nested)" : ""}
              </span>
            ) : null}
          </div>
          <div className="font-semibold">
            Estimated total: {fmtCents(display.estimated_total_cents)}
          </div>
          {event.quoted_amount_cents != null ? (
            <div className="text-xs text-gray-600 dark:text-zinc-400">
              Quoted: {fmtCents(event.quoted_amount_cents)}
              {c.preview?.margin_cents != null ? (
                <> · Margin: {fmtCents(c.preview.margin_cents)}</>
              ) : null}
            </div>
          ) : null}
          {leaves.length > 0 ? (
            <ul className="ml-4 list-disc text-xs text-gray-600 dark:text-zinc-400">
              {leaves.map((leaf) => (
                <li key={leaf.material_code}>
                  {leaf.material_code} × {leaf.quantity}: $
                  {leaf.extended_cost_usd.toFixed(2)}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          No cost data — configure labor rates and link a service-tagged mfg recipe.
        </p>
      )}

      <button
        type="button"
        className={`${ghostBtnClass} mt-3`}
        onClick={() => void c.reload()}
        disabled={c.busy}
      >
        {c.busy ? "Refreshing…" : "Refresh preview"}
      </button>

      {event.status === "complete" ? (
        <div className="mt-4 border-t border-gray-100 pt-4 dark:border-zinc-800">
          <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-zinc-100">
            Post-job materials (actual BOM)
          </h4>
          <p className="mb-2 text-xs text-gray-500 dark:text-zinc-400">
            Manual line entry for pilot. Receipt ref links to future inventory receive/OCR;
            WAC draw-down via <code>service_material_consumption_events</code> is deferred.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <input
              className={inputClass}
              placeholder="Material / part label"
              value={c.bomDraft.label}
              onChange={(e) => c.setBomDraft((d) => ({ ...d, label: e.target.value }))}
            />
            <input
              className={inputClass}
              placeholder="Qty"
              value={c.bomDraft.quantity}
              onChange={(e) => c.setBomDraft((d) => ({ ...d, quantity: e.target.value }))}
            />
            <input
              className={inputClass}
              placeholder="Unit cost (USD)"
              value={c.bomDraft.unit_cost_dollars}
              onChange={(e) => c.setBomDraft((d) => ({ ...d, unit_cost_dollars: e.target.value }))}
            />
            <input
              className={inputClass}
              placeholder="Receipt ref (optional)"
              value={c.bomDraft.receipt_ref}
              onChange={(e) => c.setBomDraft((d) => ({ ...d, receipt_ref: e.target.value }))}
            />
          </div>
          <button
            type="button"
            className={`${primaryBtnClass} mt-2`}
            disabled={c.actionBusy}
            onClick={() => void c.addBomLine()}
          >
            {c.actionBusy ? "Saving…" : "Add BOM line"}
          </button>
          {c.actualLines.length > 0 ? (
            <ul className="mt-3 divide-y divide-gray-100 dark:divide-zinc-800">
              {c.actualLines.map((line) => (
                <li key={line.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-zinc-100">{line.label}</div>
                    <div className="text-xs text-gray-500 dark:text-zinc-400">
                      × {line.quantity}
                      {line.unit_cost_cents != null ? ` @ ${fmtCents(line.unit_cost_cents)}` : ""}
                      {line.receipt_ref ? ` · receipt: ${line.receipt_ref}` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={dangerBtnClass}
                    disabled={c.actionBusy}
                    onClick={() => void c.removeBomLine(line.id)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {c.actualLines.length > 0 ? (
            <p className="mt-2 text-sm font-medium text-gray-800 dark:text-zinc-200">
              Actual materials total: {fmtCents(actualTotalCents)}
            </p>
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}
